export type TableElementKey<T> = T[keyof T] | number;
export type ExpandableRow = number | number[] | 'all' | 'none';

/*
 * Define the expandable behavior
 * 'one' - Expand one at time
 * 'each' - Expand each independently
 * */
export type ExpandableBehaviorType = 'one' | 'each';
export type TableBorder = 'bottom' | 'all' | 'none';
export type TableWidth = number | 'fit' | `${number}%`;

export type TableElement<T> = {
  key: TableElementKey<T>;
  data: T;
  selected: boolean;
  trackBy: TableElementKey<T>;
  hide: boolean;
};

export type InfiniteScrollOptions = {
  distance: number;
  throttle: number;
};

export type TableSelectionActionType = 'all' | 'none' | 'selection' | 'input';
export type TableSelectionAction<T> = {
  action: TableSelectionActionType;
  data: TableElementKey<T>[];
};

export type TableColumnFilterItem = {
  text: string;
  value: string;
};

export type TableColumnSortOption = 'ascending' | 'descending' | 'none';

export type SortState = {
  field: string;
  type: TableColumnSortOption;
};

export type TableColumnEvent = {
  column: string;
  field: string;
  sort: TableColumnSortOption;
  searchText?: string;
  searchField?: string;
  filter: Filter<Record<string, unknown>>[];
};

export const EMPTY_TABLE_EVENT: TableColumnEvent = {
  column: '',
  field: '',
  sort: 'none',
  filter: [],
};

export type TableStateFilterEvent = {
  field: string;
  type: 'search' | 'option' | 'custom' | 'none';
  values: string[];
};

export type TableStateEvent = {
  sort: SortState;
  filter: TableStateFilterEvent[];
  searchText?: string;
  searchField?: string;
};

export type MatchValues = {
  field: string;
  type: 'search' | 'option' | 'custom' | 'none';
  values: string[];
};

export interface Filterable<T> {
  allow(element: T): boolean;
}

export interface Matchable {
  match(field: string): boolean;
  values(): MatchValues;
}

export interface Filter<T> extends Filterable<T>, Matchable {
  apply(): boolean;
}

export interface FilterDelegate<T extends Record<string, unknown>> {
  filter(field: string, values: string[]): Filterable<T>;
}

export type SearchType = 'start' | 'contains';
